import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  MenuBook,
  FolderOpen,
  Assignment,
  Search,
  BarChart,
  AccountCircle,
  Logout,
  ExpandMore,
  Notifications,
  Person,
  ShoppingCart,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
  Description as DescriptionIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { Badge } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../stores/useAppStore';
import { useCartStore } from '../stores/useCartStore';
import { SimpleNotificationBell } from './SimpleNotificationBell';
import { logger } from '../utils/logger';

import {
  canManageBooks,
  canManageResources,
  canManageCategories,
  canManageSubjects,
  canManageLessonPlans,
  canManageLessonPlanTemplates,
  canViewReports,
  canManageUsers,
  canManageBackups,
  canBorrowBooks,
  isTeacher,
  isStudent
} from '../utils/rbac';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAppStore();
  const { getTotalItems } = useCartStore();
  const cartItemCount = getTotalItems();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, menuName: string) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuName);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleCartClick = () => {
    navigate('/cart');
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
    setMobileOpen(false);
  };

  // Get user roles for permission checking
  const userRoles = user?.roles || [];
  const isTeacherRole = isTeacher(userRoles);
  const isStudentRole = isStudent(userRoles);

  // IMPORTANT:
  // Users can have MULTIPLE roles (e.g. Librarian + Teacher) and should get the UNION of permissions.
  // So we only apply "teacher/student simplified UI" when they don't have any back-office role.
  const hasBackOfficeRole =
    canManageBooks(userRoles) ||
    canManageResources(userRoles) ||
    canManageCategories(userRoles) ||
    canManageSubjects(userRoles) ||
    canManageUsers(userRoles) ||
    canViewReports(userRoles) ||
    canManageBackups(userRoles);
  const isBasicUser = (isTeacherRole || isStudentRole) && !hasBackOfficeRole;

  // Get menu name for books based on role
  const getBooksMenuName = () => {
    if (isBasicUser) {
      return 'Thư viện sách';
    }
    return 'QL sách';
  };

  // Filter menu items based on user roles
  const menuItems = [
    {
      name: 'Trang chủ',
      path: '/dashboard',
      icon: <Dashboard />,
      hasSubmenu: false,
      visible: true // Everyone can see dashboard
    },
    {
      name: getBooksMenuName(),
      path: '/books',
      icon: <MenuBook />,
      hasSubmenu: !isBasicUser, // Only hide submenu for Teacher/Student-only users
      visible: canManageBooks(userRoles) || canBorrowBooks(userRoles), // Can manage or borrow
      submenu: !isBasicUser ? [
        { name: 'Danh sách sách', path: '/books', visible: true },
        { name: 'Sách mượn', path: '/books/loans', visible: canManageBooks(userRoles) },
        { name: 'QL danh mục', path: '/categories', visible: canManageCategories(userRoles) }
      ] : []
    },
    {
      name: 'QL tài nguyên',
      path: '/resources',
      icon: <FolderOpen />,
      hasSubmenu: !isBasicUser, // Only hide submenu for Teacher/Student-only users
      visible: canManageResources(userRoles) || canBorrowBooks(userRoles), // Can manage or view/download
      submenu: !isBasicUser ? [
        { name: 'Danh sách tài nguyên', path: '/resources', visible: true },
        { name: 'QL chủ đề', path: '/resource-topics', visible: canManageCategories(userRoles) }
      ] : []
    },
    {
      name: 'Giáo án',
      path: '/lesson-plans',
      icon: <Assignment />,
      hasSubmenu: canManageSubjects(userRoles) || canManageLessonPlanTemplates(userRoles), // Show submenu only for Admin, Approver, or Librarian
      visible: canManageLessonPlans(userRoles) || canManageLessonPlanTemplates(userRoles), // Can manage lesson plans or templates
      submenu: (canManageSubjects(userRoles) || canManageLessonPlanTemplates(userRoles)) ? [
        { name: 'Danh sách giáo án', path: '/lesson-plans', visible: true },
        { name: 'QL Môn học', path: '/subjects', visible: canManageSubjects(userRoles) },
        { name: 'QL Mẫu giáo án', path: '/lesson-plans/templates', visible: canManageLessonPlanTemplates(userRoles) }
      ] : []
    },
    {
      name: 'Công văn',
      path: '/official-documents',
      icon: <DescriptionIcon />,
      hasSubmenu: false,
      visible: canManageResources(userRoles) || canBorrowBooks(userRoles) // Same visibility as resources
    },
    {
      name: 'Báo cáo',
      path: '/reports',
      icon: <BarChart />,
      hasSubmenu: false,
      // canViewReports already excludes Teacher-only users; keep union behavior for multi-role users.
      visible: canViewReports(userRoles)
    },
    {
      name: 'Tìm kiếm',
      path: '/search',
      icon: <Search />,
      hasSubmenu: false,
      visible: canBorrowBooks(userRoles) // All authenticated users can search
    }
  ].filter(item => item.visible); // Filter out invisible items

  const drawer = (
    <Box>
      <Toolbar>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <Box
            component="img"
            src="/favicon.png"
            alt="Thư Viện Số"
            sx={{
              height: 40,
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.name}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  minHeight: 48,
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                />
              </ListItemButton>
            </ListItem>
            {item.hasSubmenu && (
              <List component="div" disablePadding>
                {item.submenu?.filter(subItem => subItem.visible !== false).map((subItem) => (
                  <ListItem key={subItem.name} disablePadding>
                    <ListItemButton
                      sx={{
                        pl: 4,
                        minHeight: 40,
                        py: 1,
                        px: 2,
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                      onClick={() => handleNavigation(subItem.path)}
                    >
                      <ListItemText
                        primary={subItem.name}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: 400
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static" sx={{
        zIndex: theme.zIndex.drawer + 1,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        flexShrink: 0 // Prevent header from shrinking
      }}>
        <Toolbar sx={{
          minHeight: { xs: 56, sm: 64 },
          maxHeight: { xs: 56, sm: 64 }, // Fixed height to prevent size changes
          px: { xs: 1, sm: 2, md: 3 },
          display: 'flex',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          position: 'relative',
          flexWrap: 'nowrap',
          flexShrink: 0 // Prevent toolbar from shrinking
        }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, flexShrink: 0 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            component="div"
            sx={{
              flexGrow: 0,
              flexShrink: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              mr: { xs: 1, sm: 2, md: 3 },
              minWidth: 'fit-content'
            }}
            onClick={() => navigate('/dashboard')}
          >
            <Box
              component="img"
              src="/favicon.png"
              alt="Thư Viện Số"
              sx={{
                height: 40,
                width: 'auto',
                objectFit: 'contain',
                flexShrink: 0
              }}
            />
          </Box>

          {!isMobile && (
            <Box sx={{
              display: 'flex',
              gap: 1,
              flexGrow: 1,
              flexShrink: 1,
              justifyContent: 'flex-start',
              alignItems: 'center',
              minWidth: 0,
              overflow: 'hidden'
            }}>
              {menuItems.map((item) => (
                <React.Fragment key={item.name}>
                  {item.hasSubmenu ? (
                    <>
                      <Button
                        color="inherit"
                        onClick={(e) => handleMenuClick(e, item.name)}
                        endIcon={<ExpandMore />}
                        sx={{
                          backgroundColor: location.pathname.startsWith(item.path)
                            ? 'rgba(255,255,255,0.1)'
                            : 'transparent',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          minWidth: 'fit-content',
                          height: 40,
                          px: 1.5,
                          py: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textTransform: 'none',
                          lineHeight: 1.5,
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.08)'
                          },
                          '& .MuiButton-endIcon': {
                            marginLeft: 0.5
                          }
                        }}
                      >
                        {item.name}
                      </Button>
                      <Menu
                        anchorEl={anchorEl}
                        open={activeMenu === item.name}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        PaperProps={{
                          sx: {
                            minWidth: 200,
                            maxWidth: 300,
                            mt: 0.5,
                            boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            '& .MuiMenuItem-root': {
                              minHeight: 40,
                              height: 40,
                              fontSize: '0.875rem',
                              fontWeight: 400,
                              px: 2,
                              py: 1,
                              lineHeight: 1.5
                            }
                          }
                        }}
                      >
                        {item.submenu?.filter(subItem => subItem.visible !== false).map((subItem) => (
                          <MenuItem
                            key={subItem.name}
                            onClick={() => handleNavigation(subItem.path)}
                            selected={location.pathname === subItem.path}
                            sx={{
                              minHeight: 40,
                              height: 40,
                              fontSize: '0.875rem',
                              fontWeight: 400,
                              px: 2,
                              py: 1,
                              lineHeight: 1.5,
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                                fontWeight: 500,
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.16)'
                                }
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            {subItem.name}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : (
                    <Button
                      color="inherit"
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        backgroundColor: location.pathname === item.path
                          ? 'rgba(255,255,255,0.1)'
                          : 'transparent',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        minWidth: 'fit-content',
                        height: 40,
                        px: 1.5,
                        py: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        textTransform: 'none',
                        lineHeight: 1.5,
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.08)'
                        }
                      }}
                    >
                      {item.name}
                    </Button>
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}

          <Box sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
            minWidth: 'fit-content'
          }}>
            <SimpleNotificationBell />
            <IconButton color="inherit" onClick={handleCartClick} title="Giỏ sách" sx={{ flexShrink: 0 }}>
              <Badge badgeContent={cartItemCount} color="error">
                <ShoppingCart />
              </Badge>
            </IconButton>
            <IconButton color="inherit" onClick={handleProfileClick} sx={{ flexShrink: 0 }}>
              <AccountCircle />
            </IconButton>
            {/* Logout button removed - only available in user avatar dropdown */}
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchorEl}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => { handleNavigation('/profile'); handleUserMenuClose(); }}>
          <Person sx={{ mr: 1 }} fontSize="small" />
          Hồ sơ
        </MenuItem>
        <MenuItem onClick={() => { handleNavigation('/settings'); handleUserMenuClose(); }}>
          <SettingsIcon sx={{ mr: 1 }} fontSize="small" />
          Cài đặt
        </MenuItem>
        {canManageUsers(userRoles) && (
          <MenuItem onClick={() => { handleNavigation('/user-management'); handleUserMenuClose(); }}>
            <GroupIcon sx={{ mr: 1 }} fontSize="small" />
            QL người dùng
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { handleLogout(); handleUserMenuClose(); }}>
          <Logout sx={{ mr: 1 }} fontSize="small" />
          Đăng xuất
        </MenuItem>
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
